import './TextBase.scss';
import { Component } from '@components/Component/Component.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { zoomHandler } from '@components/Utility/index.js';
import { ENV } from '@components/Environment.js';
import { combineLatestWith, debounceTime, startWith, takeUntil } from 'rxjs/operators';
/**
 * `Hint` component is for adding text to a form without any input or anything. Often placed below a grid or set of inputs to provide some context or instruction.
 */
export class TextBase extends Component {
    constructor() {
        super(...arguments);
        this.componentName = 'TextBase';
        /**
         * The HTML text element type
         * @protected
         */
        this._textElementType = 'p';
        this._textElementAttributes = {};
        this._bold = false;
    }
    get zoomTemplate() { return this._zoomTemplate || ''; }
    set zoomTemplate(value) {
        this._propertySet('zoomTemplate', value);
        this.renderZoom();
    }
    get zoomConditions() { return this._zoomConditions; }
    set zoomConditions(value) {
        this._propertySet('zoomConditions', value);
        this.renderZoom();
    }
    get textElementType() { return this._textElementType; }
    set textElementType(textElementType) {
        this._propertySet('textElementType', textElementType);
        if (this.drawn) {
            this.renderTextElement();
        }
    }
    get textElementAttributes() { return this._textElementAttributes; }
    set textElementAttributes(textElementAttributes) {
        this._propertySet('textElementAttributes', textElementAttributes);
        if (this.drawn) {
            this.renderTextElement();
        }
    }
    get bold() { return this._bold; }
    set bold(toBold) {
        this._propertySet('bold', toBold);
        if (this.drawn) {
            this.renderBold();
        }
    }
    get align() { return this._align; }
    set align(align) {
        this._propertySet('align', align);
        if (this.drawn) {
            this.renderAlign();
        }
    }
    get caption() { return this._caption || ''; }
    set caption(value) {
        this._propertySet('caption', value);
        this.renderCaption();
    }
    get tooltip() { return this._tooltip || ''; }
    set tooltip(value) {
        if ((value || '') != (this._tooltip || '')) {
            this._propertySet('tooltip', value);
            this.renderTooltip();
        }
    }
    captionTypesSet() {
        this.captionTypes = {
            caption: 'caption',
            tooltip: 'tooltip'
        };
    }
    draw() {
        super.draw();
        if (this.bold) {
            this.renderBold();
        }
        if (this._align) {
            this.renderAlign();
        }
        // if (this.caption) {
        // 	this.renderCaption();
        // }
    }
    unlinkRecord(keepState = false) {
        if (this.recordChangeSubscription) {
            this.recordChangeSubscription.unsubscribe();
            this.recordChangeSubscription = undefined;
        }
        super.unlinkRecord(keepState);
    }
    linkRecord(record) {
        super.linkRecord(record);
        if (this.recordChangeSubscription) {
            this.recordChangeSubscription.unsubscribe();
        }
        this.recordChangeSubscription = record.load$.pipe(combineLatestWith(record.allChangesGet$().pipe(startWith(undefined))), debounceTime(100), takeUntil(this.destroy$))
            .subscribe(() => {
            this.handleRecordChanges();
        });
    }
    handleRecordChanges() {
        this.renderZoom();
    }
    renderZoom() {
        if (!this.textElement) {
            return;
        }
        if (this.zoomEventId) {
            this.unRegisterEvent(this.zoomEventId);
        }
        const conditions = this.zoomConditions;
        const template = this.zoomTemplate;
        const conditionsMgr = DIContainer.getInstance('ConditionsManager');
        let templateReady = !!template;
        if (templateReady && this.record) {
            // ensure we have all the tokens that we need
            const globalTokenRegex = /{{(.*?)}}/g;
            let match;
            while ((match = globalTokenRegex.exec(template)) !== null) {
                const tag = match[1];
                const [tokenName] = tag.split(':');
                if (!this.record.get(tokenName) && !template.startsWith('/form/sqlview')) {
                    templateReady = false;
                }
            }
        }
        if (templateReady && (!conditions || conditionsMgr.validateConditions(conditions))) {
            this.textElement.classList.add('link');
            this.zoomEventId = this.registerEvent('click', this.textElement, (event) => {
                this.trigger('click', {
                    'defaultEventHandler': () => {
                        let target = '_top';
                        if (ENV('appName') === 'formbuilder') {
                            target = '_blank';
                        }
                        zoomHandler(template, {
                            target: target,
                            data: this.record?.data,
                            initiator: this,
                            event
                        }).subscribe();
                    }
                });
            });
        }
        else {
            this.textElement.classList.remove('link');
        }
    }
    renderTextElement() {
        if (this.textElement && this.rootElement.contains(this.textElement)) {
            this.rootElement.removeChild(this.textElement);
        }
        this.textElement = document.createElement(this._textElementType);
        for (const [attr, value] of Object.entries(this.textElementAttributes)) {
            this.textElement.setAttribute(attr, value);
        }
        this.renderCaption();
        if (this._tooltip) {
            this.renderTooltip();
        }
        if (this.zoomTemplate) {
            this.renderZoom();
        }
        this.rootElement.appendChild(this.textElement);
    }
    renderCaption() {
        if (this.textElement) {
            // Clear the current text content
            this.textElement.textContent = '';
            // Split the caption by new lines
            const lines = (this._caption || '').split('\n');
            // Append each line as a text node with a line break
            lines.forEach((line, index) => {
                if (index > 0) {
                    this.textElement.appendChild(document.createElement('br'));
                }
                this.textElement.appendChild(document.createTextNode(line));
            });
        }
    }
    renderTooltip() {
        if (this.textElement) {
            this.registerTooltip(this.textElement, {
                tooltip: this.tooltip
            });
        }
    }
    renderBold() {
        if (this._bold) {
            this.rootElement?.classList.add('bold');
        }
        else {
            this.rootElement?.classList.remove('bold');
        }
    }
    renderAlign() {
        this.rootElement?.classList.remove('left');
        this.rootElement?.classList.remove('center');
        this.rootElement?.classList.remove('right');
        if (this._align) {
            this.rootElement?.classList.add(this._align);
        }
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        properties.bold = ({
            id: 'bold',
            value: this._bold,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_textbase_bold',
            propertyGroup: '',
            saveFlag: () => this._bold
        });
        properties.align = ({
            id: 'align',
            value: this._align,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: 'left', display: 'Left' },
                    { value: 'center', display: 'Center' },
                    { value: 'right', display: 'Right' }
                ]
            },
            captionKey: 'formbuilder_textbase_align',
            propertyGroup: '',
            saveFlag: () => !!this._align
        });
        properties.textElementType = ({
            id: 'textElementType',
            value: this._textElementType,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: 'p', display: 'Paragraph - p' },
                    { value: 'h1', display: 'Heading 1 - h1' },
                    { value: 'h2', display: 'Heading 2 - h2' },
                    { value: 'h3', display: 'Heading 3 - h3' },
                    { value: 'h4', display: 'Heading 4 - h4' },
                    { value: 'h5', display: 'Heading 5 - h5' },
                    { value: 'h6', display: 'Heading 6 - h6' },
                    { value: 'label', display: 'Label - label' },
                    // {value: 'a', display: 'Anchor - a'},
                    { value: 'span', display: 'Span' },
                    { value: 'div', display: 'Division - div' },
                    { value: 'em', display: 'Emphasis - em' },
                    { value: 'strong', display: 'Strong' },
                    { value: 'blockquote', display: 'Blockquote' },
                    { value: 'pre', display: 'Preformatted Text - pre' },
                    { value: 'code', display: 'Code' },
                    // {value: 'ul', display: 'Unordered List - ul'},
                    // {value: 'ol', display: 'Ordered List - ol'},
                    // {value: 'li', display: 'List Item - li'},
                    // {value: 'dl', display: 'Definition List - dl'},
                    // {value: 'dt', display: 'Definition Term - dt'},
                    // {value: 'dd', display: 'Definition Description - dd'},
                    { value: 'small', display: 'Small' },
                    { value: 'cite', display: 'Citation - cite' },
                    { value: 'abbr', display: 'Abbreviation - abbr' },
                    { value: 'address', display: 'Address - address' }
                ]
            },
            captionKey: 'formbuilder_textbase_textelementtype',
            propertyGroup: '',
            saveFlag: this._textElementType !== 'p' // if its not the default type, we need to save it
        });
        properties.zoomTemplate = {
            id: 'zoomTemplate',
            value: this.zoomTemplate,
            componentType: 'Input',
            componentProperties: {},
            captionKey: 'formbuilder_zoomtemplate',
            propertyGroup: 'caption',
            propertyIndex: 7,
            saveFlag: !!this.zoomTemplate
        };
        properties.zoomConditions = {
            id: 'zoomConditions',
            value: this.zoomConditions,
            componentType: 'ConditionsEditor',
            componentProperties: {
                labelPosition: 'top'
            },
            captionKey: 'formbuilder_zoomconditions',
            propertyGroup: 'caption',
            propertyIndex: 8,
            saveFlag: !!this.zoomConditions
        };
        return properties;
    }
    /**
     * Returns an empty template as the hint is built programmatically
     * @returns {string} - An empty string
     */
    template() {
        this.renderTextElement();
        this.rootElement.classList.add('s360TextBase');
        return '';
    }
}
TextBase.VERSION = '$Revision: 112048 $';
